import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Tag, Trash2, Copy, Plus } from "lucide-react";

export default function AdminPromoCodesPage() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "",
    percent_off: 25,
    duration: "once",
    duration_in_months: 3,
    max_redemptions: "",
    expires_days: "",
    name: "",
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/promo-codes");
      setCodes(r.data.codes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load promo codes");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    refresh();
  }, [user]);

  if (user?.role !== "admin") return <Navigate to="/app/dashboard" />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Code required"); return; }
    setCreating(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        percent_off: Number(form.percent_off),
        duration: form.duration,
        name: form.name || undefined,
      };
      if (form.duration === "repeating") body.duration_in_months = Number(form.duration_in_months);
      if (form.max_redemptions) body.max_redemptions = Number(form.max_redemptions);
      if (form.expires_days) body.expires_days = Number(form.expires_days);
      await api.post("/admin/promo-codes", body);
      toast.success(`Created ${body.code}`);
      setForm({ ...form, code: "", name: "" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this promo code? Existing customers keep their discount.")) return;
    try {
      await api.post(`/admin/promo-codes/${id}/deactivate`, {});
      toast.success("Deactivated");
      refresh();
    } catch (e) { toast.error("Failed"); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  return (
    <div className="space-y-6" data-testid="admin-promo-codes-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Admin · Promo Codes</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Discount Codes.</h1>
        <p className="text-[13px] text-[#b8ac96] mt-2">Create Stripe-backed promo codes to run launch promos, founder discounts, or partner deals. Customers enter the code on your Pricing page and at Stripe Checkout.</p>
      </div>

      {/* Create Form */}
      <form onSubmit={submit} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5 space-y-4" data-testid="create-promo-form">
        <div className="font-display text-lg font-semibold flex items-center gap-2"><Plus size={16} className="text-[#d4a574]" /> Create Promo Code</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Code (customer-facing)</label>
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FOUNDER25" data-testid="promo-code-field" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] uppercase font-mono focus:outline-none focus:border-[#d4a574]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Percent Off</label>
            <input required type="number" min="1" max="100" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} data-testid="promo-percent-field" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Duration</label>
            <select value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="promo-duration-field" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]">
              <option value="once">Once (first invoice only)</option>
              <option value="repeating">Repeating (N months)</option>
              <option value="forever">Forever</option>
            </select>
          </div>
          {form.duration === "repeating" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Months</label>
              <input type="number" min="1" value={form.duration_in_months} onChange={(e) => setForm({ ...form, duration_in_months: e.target.value })} className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Max Redemptions (optional)</label>
            <input type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="100" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Expires in Days (optional)</label>
            <input type="number" min="1" value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: e.target.value })} placeholder="30" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Internal Name (optional)</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Founder's Launch 25%" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          </div>
        </div>
        <button type="submit" disabled={creating} data-testid="create-promo-submit" className="px-5 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988] disabled:opacity-60 flex items-center gap-2">
          {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create Promo Code
        </button>
      </form>

      {/* List */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f] flex items-center justify-between">
          <div className="font-display text-lg font-semibold">Active Codes</div>
          <button onClick={refresh} className="text-[11px] text-[#d4a574] hover:underline">Refresh</button>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[#d4a574]" /></div>
        ) : codes.length === 0 ? (
          <div className="p-8 text-center text-[#7a6d5a] text-sm">No promo codes yet. Create one above.</div>
        ) : (
          <table className="w-full text-[13px]" data-testid="promo-codes-table">
            <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Discount</th>
                <th className="text-left px-4 py-2">Duration</th>
                <th className="text-left px-4 py-2">Redeemed</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id} className="border-t border-[#33291f]" data-testid={`promo-row-${c.code}`}>
                  <td className="px-4 py-2"><span className="font-mono text-[#d4a574] font-semibold flex items-center gap-2"><Tag size={12} /> {c.code}</span></td>
                  <td className="px-4 py-2">{c.coupon.percent_off ? `${c.coupon.percent_off}% off` : `$${(c.coupon.amount_off / 100).toFixed(0)} off`}</td>
                  <td className="px-4 py-2 text-[#b8ac96]">
                    {c.coupon.duration === "repeating" ? `${c.coupon.duration_in_months} months` : c.coupon.duration}
                  </td>
                  <td className="px-4 py-2 font-mono text-[#b8ac96]">{c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${c.active ? "bg-[#34d399]/10 border border-[#34d399]/40 text-[#34d399]" : "bg-[#241d16] border border-[#4a3a2b] text-[#7a6d5a]"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => copy(c.code)} title="Copy code" data-testid={`copy-${c.code}`} className="p-1.5 text-[#b8ac96] hover:text-[#d4a574]"><Copy size={14} /></button>
                    {c.active && <button onClick={() => deactivate(c.id)} title="Deactivate" data-testid={`deactivate-${c.code}`} className="p-1.5 text-[#b8ac96] hover:text-[#f87171]"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-[#7a6d5a] font-mono">
        Tip: share codes like <span className="text-[#d4a574]">FOUNDER25</span> in launch emails, LinkedIn posts, or podcast sponsorships. Track redemptions in this table.
      </div>
    </div>
  );
}
